import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import Badge from "../components/ui/Badge";
import styles from "./Finance.module.css";

const fmt = (n) =>
  `KES ${Number(n ?? 0).toLocaleString("en-KE", { minimumFractionDigits: 0 })}`;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Finance() {
  const { isAdmin, profile } = useAuth();
  const [clients,     setClients]     = useState([]);
  const [employees,   setEmployees]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeTab,   setActiveTab]   = useState("overview");
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter,  setYearFilter]  = useState(String(new Date().getFullYear()));
  const [consultantFilter, setConsultantFilter] = useState("");

  useEffect(() => { if (profile) load(); }, [profile]); // eslint-disable-line

  async function load() {
    setLoading(true);
    let q = supabase
      .from("clients")
      .select("*, consultant:employees(id, full_name)")
      .order("date_started", { ascending: false });
    if (!isAdmin) q = q.eq("consultant_id", profile.id);

    const [{ data: cd }, { data: ed }] = await Promise.all([
      q,
      isAdmin
        ? supabase.from("employees").select("id,full_name").eq("role","staff").order("full_name")
        : Promise.resolve({ data: [] }),
    ]);
    setClients(cd ?? []);
    setEmployees(ed ?? []);
    setLoading(false);
  }

  // Date + consultant filtered clients
  const filtered = clients.filter((c) => {
    if (!c.date_started) return true;
    const d = new Date(c.date_started);
    const okYear       = !yearFilter       || d.getFullYear() === Number(yearFilter);
    const okMonth      = !monthFilter      || d.getMonth()    === Number(monthFilter);
    const okConsultant = !consultantFilter || c.consultant?.id === consultantFilter;
    return okYear && okMonth && okConsultant;
  });

  // KPI stats
  const totalRevenue     = filtered.reduce((s,c) => s + Number(c.total_amount    ?? 0), 0);
  const totalCollected   = filtered.reduce((s,c) => s + Number(c.amount_received ?? 0), 0);
  const totalOutstanding = filtered.reduce((s,c) => s + Number(c.balance_due    ?? 0), 0);
  const paidCount        = filtered.filter((c) => c.payment_status === "Paid").length;
  const partialCount     = filtered.filter((c) => c.payment_status === "Partial").length;
  const unpaidCount      = filtered.filter((c) => c.payment_status === "Unpaid").length;
  const collectionRate   = totalRevenue > 0
    ? ((totalCollected / totalRevenue) * 100).toFixed(1) : "0.0";

  // By consultant
  const byConsultant = employees.map((emp) => {
    const mine = filtered.filter((c) => c.consultant?.id === emp.id);
    return {
      id:          emp.id,
      name:        emp.full_name,
      count:       mine.length,
      revenue:     mine.reduce((s,c) => s + Number(c.total_amount    ?? 0), 0),
      collected:   mine.reduce((s,c) => s + Number(c.amount_received ?? 0), 0),
      outstanding: mine.reduce((s,c) => s + Number(c.balance_due    ?? 0), 0),
      paid:        mine.filter((c) => c.payment_status === "Paid").length,
    };
  }).filter((r) => r.count > 0).sort((a,b) => b.collected - a.collected);

  // By service
  const serviceMap = {};
  filtered.forEach((c) => {
    const svc = c.service_type ?? "Unknown";
    if (!serviceMap[svc]) serviceMap[svc] = { count:0, revenue:0, collected:0, outstanding:0 };
    serviceMap[svc].count++;
    serviceMap[svc].revenue     += Number(c.total_amount    ?? 0);
    serviceMap[svc].collected   += Number(c.amount_received ?? 0);
    serviceMap[svc].outstanding += Number(c.balance_due    ?? 0);
  });
  const byService = Object.entries(serviceMap)
    .map(([name,v]) => ({ name,...v }))
    .sort((a,b) => b.collected - a.collected);

  // Monthly trend (always uses full-year clients, not date-filtered)
  const currentYear = Number(yearFilter || new Date().getFullYear());
  const monthlyData = MONTHS.map((month, idx) => {
    const mc = clients.filter((c) => {
      if (!c.date_started) return false;
      const d = new Date(c.date_started);
      return d.getFullYear() === currentYear && d.getMonth() === idx;
    });
    return {
      month,
      count:     mc.length,
      collected: mc.reduce((s,c) => s + Number(c.amount_received ?? 0), 0),
      revenue:   mc.reduce((s,c) => s + Number(c.total_amount    ?? 0), 0),
    };
  });
  const maxMonthly = Math.max(...monthlyData.map((m) => m.collected), 1);

  // CSV export
  function exportCSV(rows, filename) {
    if (!rows.length) { alert("No data to export."); return; }
    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => `"${r[h] ?? ""}"`).join(",")),
    ].join("\n");
    const a    = document.createElement("a");
    a.href     = URL.createObjectURL(new Blob([csv], { type:"text/csv" }));
    a.download = `${filename}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  }

  const toCSVRows = (rows) => rows.map((c) => ({
    "Name":           c.full_name,
    "Phone":          c.phone,
    "Email":          c.email ?? "",
    "Service":        c.service_type,
    "Country":        c.destination_country ?? "",
    "Job Title":      c.job_title ?? "",
    "Consultant":     c.consultant?.full_name ?? "",
    "Total (KES)":    c.total_amount,
    "Received (KES)": c.amount_received,
    "Balance (KES)":  c.balance_due,
    "Payment Status": c.payment_status,
    "Client Status":  c.status,
    "Date Started":   c.date_started ?? "",
  }));

  const years = [...new Set(
    clients.map((c) => c.date_started?.slice(0,4)).filter(Boolean)
  )].sort((a,b) => b - a);

  const TABS = [
    { key: "overview",     label: "📊 Overview"        },
    { key: "byConsultant", label: "👤 By Consultant", adminOnly: true },
    { key: "byService",    label: "🗂 By Service"      },
    { key: "transactions", label: "📋 Transactions"    },
  ].filter((t) => !t.adminOnly || isAdmin);

  if (loading) return <div className={styles.loading}>Loading finance data…</div>;

  return (
    <div className={styles.page}>

      {/* ── Filter bar ── */}
      <div className={styles.filterBar}>
        <select className={styles.filterSel} value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}>
          <option value="">All Years</option>
          {years.map((y) => <option key={y}>{y}</option>)}
        </select>

        <select className={styles.filterSel} value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}>
          <option value="">All Months</option>
          {MONTHS.map((m,i) => <option key={i} value={i}>{m}</option>)}
        </select>

        {isAdmin && (
          <select className={styles.filterSel} value={consultantFilter}
            onChange={(e) => setConsultantFilter(e.target.value)}>
            <option value="">All Consultants</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.full_name}</option>
            ))}
          </select>
        )}

        {(monthFilter !== "" || yearFilter !== String(new Date().getFullYear()) || consultantFilter) && (
          <button className={styles.clearBtn}
            onClick={() => {
              setMonthFilter("");
              setYearFilter(String(new Date().getFullYear()));
              setConsultantFilter("");
            }}>
            ✕ Clear
          </button>
        )}

        <div className={styles.filterNote}>
          <strong>{filtered.length}</strong> client{filtered.length !== 1 ? "s" : ""}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div className={styles.kpiGrid}>
        <div className={styles.kpi}>
          <div className={styles.kpiLabel}>Total Revenue</div>
          <div className={styles.kpiValue}>{fmt(totalRevenue)}</div>
          <div className={styles.kpiSub}>{filtered.length} clients</div>
        </div>
        <div className={`${styles.kpi} ${styles.kpiGreen}`}>
          <div className={styles.kpiLabel}>Collected</div>
          <div className={styles.kpiValue}>{fmt(totalCollected)}</div>
          <div className={styles.kpiSub}>Rate: {collectionRate}%</div>
        </div>
        <div className={`${styles.kpi} ${styles.kpiOrange}`}>
          <div className={styles.kpiLabel}>Outstanding</div>
          <div className={styles.kpiValue}>{fmt(totalOutstanding)}</div>
          <div className={styles.kpiSub}>{partialCount + unpaidCount} pending</div>
        </div>
        <div className={`${styles.kpi} ${styles.kpiNavy}`}>
          <div className={styles.kpiLabel}>Payment Breakdown</div>
          <div className={styles.pills}>
            <span className={styles.pillGreen}>✓ {paidCount} Paid</span>
            <span className={styles.pillOrange}>◑ {partialCount} Partial</span>
            <span className={styles.pillRed}>○ {unpaidCount} Unpaid</span>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className={styles.tabs}>
        {TABS.map(({ key, label }) => (
          <button key={key}
            className={`${styles.tab} ${activeTab === key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview: Monthly Bar Chart ── */}
      {activeTab === "overview" && (
        <div className={styles.reportCard}>
          <div className={styles.reportHead}>
            <h3>Monthly Collection — {currentYear}</h3>
            <div className={styles.reportActions}>
              <button className={styles.exportBtn}
                onClick={() => exportCSV(toCSVRows(filtered), "clients-report")}>
                ⬇ Export CSV
              </button>
            </div>
          </div>
          <div className={styles.barChart}>
            {monthlyData.map((m) => (
              <div key={m.month} className={styles.barCol}>
                <div className={styles.barWrap}>
                  <div
                    className={styles.bar}
                    style={{ height: `${(m.collected / maxMonthly) * 100}%` }}
                  >
                    <div className={styles.barTip}>
                      {m.month}: {fmt(m.collected)}
                    </div>
                  </div>
                </div>
                <div className={styles.barLabel}>{m.month}</div>
                {m.count > 0 && <div className={styles.barCount}>{m.count}</div>}
              </div>
            ))}
          </div>
          <div className={styles.txMeta}>
            Bar height = KES collected · number below bar label = clients that month
          </div>
        </div>
      )}

      {/* ── By Consultant ── */}
      {activeTab === "byConsultant" && isAdmin && (
        <div className={styles.reportCard}>
          <div className={styles.reportHead}>
            <h3>Consultant Performance</h3>
            <button className={styles.exportBtn}
              onClick={() => exportCSV(
                byConsultant.map((r) => ({
                  Consultant: r.name, Clients: r.count,
                  "Revenue (KES)": r.revenue,
                  "Collected (KES)": r.collected,
                  "Outstanding (KES)": r.outstanding,
                  "Fully Paid": r.paid,
                })),
                "consultant-report"
              )}>
              ⬇ Export CSV
            </button>
          </div>
          {byConsultant.length === 0 ? (
            <div className={styles.empty}>No data for selected period.</div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Consultant</th><th>Clients</th><th>Revenue</th>
                    <th>Collected</th><th>Outstanding</th><th>Paid</th><th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {byConsultant.map((r) => (
                    <tr key={r.id}>
                      <td><strong>{r.name}</strong></td>
                      <td>{r.count}</td>
                      <td>{fmt(r.revenue)}</td>
                      <td className={styles.green}>{fmt(r.collected)}</td>
                      <td className={styles.orange}>{fmt(r.outstanding)}</td>
                      <td>{r.paid}</td>
                      <td>
                        <div className={styles.rateWrap}>
                          <div className={styles.rateBar}>
                            <div className={styles.rateFill}
                              style={{ width: r.revenue > 0
                                ? `${(r.collected/r.revenue)*100}%` : "0%" }} />
                          </div>
                          <span className={styles.ratePct}>
                            {r.revenue > 0
                              ? ((r.collected/r.revenue)*100).toFixed(0) : 0}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Consultant Cards */}
              <div className={styles.mobileList}>
                {byConsultant.map((r) => (
                  <div key={r.id} className={styles.consultantCard}>
                    <div className={styles.consultantHeader}>
                      <div className={styles.consultantAvatar}>
                        {r.name?.[0]?.toUpperCase() ?? "?"}
                      </div>
                      <div className={styles.consultantInfo}>
                        <div className={styles.consultantName}>{r.name}</div>
                        <div className={styles.consultantSub}>{r.count} clients · {r.paid} fully paid</div>
                      </div>
                      <span className={styles.consultantRateBadge}>
                        {r.revenue > 0 ? ((r.collected / r.revenue) * 100).toFixed(0) : 0}% rate
                      </span>
                    </div>

                    <div className={styles.consultantMetrics}>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>Revenue</span>
                        <span className={styles.metricVal}>{fmt(r.revenue)}</span>
                      </div>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>Collected</span>
                        <span className={`${styles.metricVal} ${styles.green}`}>{fmt(r.collected)}</span>
                      </div>
                      <div className={styles.metricItem}>
                        <span className={styles.metricLabel}>Outstanding</span>
                        <span className={`${styles.metricVal} ${styles.orange}`}>{fmt(r.outstanding)}</span>
                      </div>
                    </div>

                    <div className={styles.rateWrapMobile}>
                      <div className={styles.rateBar}>
                        <div
                          className={styles.rateFill}
                          style={{ width: r.revenue > 0 ? `${(r.collected / r.revenue) * 100}%` : "0%" }}
                        />
                      </div>
                      <span className={styles.ratePct}>
                        Collection Rate: {r.revenue > 0 ? ((r.collected / r.revenue) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── By Service ── */}
      {activeTab === "byService" && (
        <div className={styles.reportCard}>
          <div className={styles.reportHead}>
            <h3>Service Type Breakdown</h3>
            <button className={styles.exportBtn}
              onClick={() => exportCSV(
                byService.map((s) => ({
                  Service: s.name, Clients: s.count,
                  "Revenue (KES)": s.revenue,
                  "Collected (KES)": s.collected,
                  "Outstanding (KES)": s.outstanding,
                })),
                "service-report"
              )}>
              ⬇ Export CSV
            </button>
          </div>
          {byService.length === 0 ? (
            <div className={styles.empty}>No data for selected period.</div>
          ) : (
            <div className={styles.svcGrid}>
              {byService.map((s) => (
                <div key={s.name} className={styles.svcCard}>
                  <div className={styles.svcName}>{s.name}</div>
                  <div className={styles.svcStats}>
                    <div>
                      <div className={styles.svcStatL}>Clients</div>
                      <div className={styles.svcStatV}>{s.count}</div>
                    </div>
                    <div>
                      <div className={styles.svcStatL}>Revenue</div>
                      <div className={styles.svcStatV}>{fmt(s.revenue)}</div>
                    </div>
                    <div>
                      <div className={styles.svcStatL}>Collected</div>
                      <div className={`${styles.svcStatV} ${styles.green}`}>
                        {fmt(s.collected)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.svcStatL}>Outstanding</div>
                      <div className={`${styles.svcStatV} ${styles.orange}`}>
                        {fmt(s.outstanding)}
                      </div>
                    </div>
                  </div>
                  <div className={styles.svcProgressWrap}>
                    <div className={styles.svcProgress}>
                      <div className={styles.svcProgressFill}
                        style={{ width: s.revenue > 0
                          ? `${(s.collected/s.revenue)*100}%` : "0%" }} />
                    </div>
                    <span className={styles.svcRate}>
                      {s.revenue > 0
                        ? ((s.collected/s.revenue)*100).toFixed(1) : 0}% collected
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Transactions ── */}
      {activeTab === "transactions" && (
        <div className={styles.reportCard}>
          <div className={styles.reportHead}>
            <h3>All Transactions</h3>
            <div className={styles.reportActions}>
              <button className={styles.exportBtn}
                onClick={() => exportCSV(toCSVRows(filtered), "all-transactions")}>
                ⬇ Full Report
              </button>
              <button className={`${styles.exportBtn} ${styles.exportRed}`}
                onClick={() => exportCSV(
                  toCSVRows(filtered.filter((c) => c.payment_status !== "Paid")),
                  "outstanding-balances"
                )}>
                ⬇ Outstanding Only
              </button>
            </div>
          </div>
          <div className={styles.txMeta}>
            Showing <strong>{filtered.length}</strong> transaction
            {filtered.length !== 1 ? "s" : ""}
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Service</th>
                  {isAdmin && <th>Consultant</th>}
                  <th>Total</th>
                  <th>Received</th>
                  <th>Balance</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 9 : 8} className={styles.empty}>
                      No transactions for this period.
                    </td>
                  </tr>
                ) : filtered.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.full_name}</strong>
                      <div className={styles.muted}>{c.phone}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 12 }}>{c.service_type}</div>
                      {c.destination_country &&
                        <div className={styles.muted}>{c.destination_country}</div>}
                    </td>
                    {isAdmin && <td>{c.consultant?.full_name ?? "—"}</td>}
                    <td>{fmt(c.total_amount)}</td>
                    <td className={styles.green}>{fmt(c.amount_received)}</td>
                    <td className={Number(c.balance_due) > 0 ? styles.orange : ""}>
                      <strong>{fmt(c.balance_due)}</strong>
                    </td>
                    <td><Badge label={c.payment_status} /></td>
                    <td><Badge label={c.status} /></td>
                    <td style={{ whiteSpace:"nowrap", fontSize:12 }}>
                      {c.date_started ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Transaction Cards */}
            <div className={styles.mobileList}>
              {filtered.map((c) => (
                <div key={c.id} className={styles.txCard}>
                  <div className={styles.txHeader}>
                    <div>
                      <div className={styles.txName}>{c.full_name}</div>
                      <div className={styles.txPhone}>
                        <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}>
                          📞 {c.phone}
                        </a>
                      </div>
                    </div>
                    <Badge label={c.status} />
                  </div>

                  <div className={styles.txDetails}>
                    <div className={styles.txDetailRow}>
                      <span className={styles.txDetailLabel}>Service:</span>
                      <span className={styles.txDetailVal}>
                        {c.service_type}
                        {c.destination_country && ` (${c.destination_country})`}
                      </span>
                    </div>
                    {isAdmin && c.consultant?.full_name && (
                      <div className={styles.txDetailRow}>
                        <span className={styles.txDetailLabel}>Consultant:</span>
                        <span className={styles.txDetailVal}>{c.consultant.full_name}</span>
                      </div>
                    )}
                    {c.date_started && (
                      <div className={styles.txDetailRow}>
                        <span className={styles.txDetailLabel}>Date:</span>
                        <span className={styles.txDetailVal}>{c.date_started}</span>
                      </div>
                    )}
                  </div>

                  <div className={styles.txFinances}>
                    <div className={styles.txFinCol}>
                      <span className={styles.txFinLabel}>Total</span>
                      <span className={styles.txFinVal}>{fmt(c.total_amount)}</span>
                    </div>
                    <div className={styles.txFinCol}>
                      <span className={styles.txFinLabel}>Received</span>
                      <span className={`${styles.txFinVal} ${styles.green}`}>{fmt(c.amount_received)}</span>
                    </div>
                    <div className={styles.txFinCol}>
                      <span className={styles.txFinLabel}>Balance</span>
                      <span className={`${styles.txFinVal} ${Number(c.balance_due) > 0 ? styles.orange : ""}`}>
                        {fmt(c.balance_due)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.txFooter}>
                    <Badge label={c.payment_status} />
                    <span className={styles.txStatusNote}>{c.status}</span>
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div className={styles.empty}>No transactions for this period.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}